import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchPageSampleHtml } from '../../services/compositionApi';
import Icon from '../common/Icon';
import FormatBook from './FormatBook';

// « VOIR L'INTERIEUR » (retour utilisateur 2026-10-17 : « dans create-book l'utilisateur doit savoir a quoi va ressembler
// un livre — visuellement ils se ressemblent tous »).
//
// Montre, pour UN format : sa couverture a grande taille, une VRAIE double page ouverte (rendue par le meme code que
// l'atelier et le fichier d'impression : GET /api/catalog/page-sample.html, deux appels gauche/droite) et sa fiche
// technique (taille, couverture, reliure, papier, pages, prix). Aucune photo de catalogue fournisseur : ce qui est
// montre est ce que Bookipix imprimera.
//
// Reliure layflat : la double page s'ouvre a plat, une photo traverse les deux pages sans coupure (aucune ombre au
// pli). Dos colle : la reliure creuse le milieu (ombre au pli), donc on evite de poser un visage sur le pli.
const pageCache = new Map();

function loadPage({ formatId, side, lang }) {
  const key = `${formatId}|${side}|${lang}`;
  if (pageCache.has(key)) return Promise.resolve(pageCache.get(key));
  return fetchPageSampleHtml({ format: formatId, side, lang }).then((html) => { pageCache.set(key, html); return html; });
}

function FormatInteriorModal({ format, coverStyle, lang, ownBookId, priceLabel, selected, onClose, onChoose }) {
  const { t } = useTranslation('createBook');
  const [pages, setPages] = useState({ left: null, right: null });
  const flat = format.binding === 'layflat';
  const hard = format.reliure === 'rigide';
  const cw = Number(format.widthMm) / 10 || 21;
  const ch = Number(format.heightMm) / 10 || 28;
  const cm = (mm) => new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-FR', { maximumFractionDigits: 1 }).format(Number(mm) / 10);

  useEffect(() => {
    let cancelled = false;
    setPages({ left: null, right: null });
    ['left', 'right'].forEach((side) => {
      loadPage({ formatId: format.formatId, side, lang })
        .then((html) => { if (!cancelled) setPages((previous) => ({ ...previous, [side]: html })); })
        .catch(() => { /* non bloquant : la page reste en attente, le reste de la fiche s'affiche */ });
    });
    return () => { cancelled = true; };
  }, [format.formatId, lang]);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; };
  }, [onClose]);

  const specs = [
    [t('formatStep.specSize'), `${cm(format.widthMm)} × ${cm(format.heightMm)} cm`],
    [t('formatStep.specCover'), hard ? t('formatStep.hardcoverFull') : t('formatStep.softcoverFull')],
    [t('formatStep.specBinding'), flat ? t('formatStep.bindingLayflatLong') : t('formatStep.bindingGluedLong')],
    ...(format.paper ? [[t('formatStep.specPaper'), format.paper]] : []),
    [t('formatStep.specPages'), t('formatStep.specPagesValue', { min: format.minPages, max: format.maxPages })],
    [t('formatStep.specPrice'), `${t('formatStep.startingFrom')} ${priceLabel} ${t('formatStep.forPages', { count: format.minPages })}`]
  ];

  return (
    <div className="fim-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="fim-modal" role="dialog" aria-modal="true" aria-labelledby="fim-title">
        <div className="fim-head">
          <h3 id="fim-title">{t('formatStep.insideTitle', { name: format.nom })}</h3>
          <button type="button" className="fim-close" onClick={onClose} aria-label={t('formatStep.close')}>×</button>
        </div>

        <div className="fim-body">
          <div className="fim-cover-col">
            <div className="fim-cover">
              <FormatBook
                formatId={format.formatId}
                widthMm={format.widthMm}
                heightMm={format.heightMm}
                reliure={format.reliure}
                style={coverStyle}
                lang={lang}
                ownBookId={ownBookId}
                alt={t('formatStep.coverAlt', { name: format.nom })}
              />
            </div>
            <span className="fim-caption">{t('formatStep.coverCaption')}</span>
          </div>

          <div className="fim-spread-col">
            <div
              className={`fim-spread ${flat ? 'is-flat' : 'is-glued'}`}
              style={{ aspectRatio: `${2 * cw} / ${ch}` }}
              role="img"
              aria-label={t('formatStep.spreadAlt', { name: format.nom })}
            >
              {['left', 'right'].map((side) => (
                <div key={side} className="fim-page">
                  {pages[side]
                    ? <iframe title={t('formatStep.spreadAlt', { name: format.nom })} srcDoc={pages[side]} tabIndex={-1} aria-hidden="true" />
                    : <span className="fbk-skeleton" />}
                </div>
              ))}
              <span className="fim-fold" aria-hidden="true" />
            </div>
            <span className="fim-caption">{flat ? t('formatStep.spreadFlat') : t('formatStep.spreadGlued')}</span>
          </div>
        </div>

        <dl className="fim-specs">
          {specs.map(([label, value]) => (
            <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
          ))}
        </dl>

        <div className="fim-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('formatStep.close')}</button>
          <button type="button" className="btn btn-primary" onClick={onChoose}>
            {selected ? <Icon name="check" size={16} /> : null} {selected ? t('formatStep.chosen') : t('formatStep.chooseThis')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FormatInteriorModal;
